import { MAP_LAYERS } from './satelliteModel';

/**
 * Satellite Insights: the words. The satellite model never writes English; it
 * returns `{ key, params }` (or `{ parts: [...] }` joined with " · "), and this
 * turns them into text with the farmer app's own English strings, copied from
 * farmer-app src/i18n/strings.js (only the ones these sections can show).
 * The rendering rules are farmer-app src/components/satellite/satText.js,
 * English only.
 */

/* Generated from farmer-app src/i18n/strings.js (English). */
const EN = {
  retry: 'Try again',
  loading: 'Loading…',
  harvest: 'Harvest',
  needsAction: 'Needs action',
  watch: 'Watch',
  good: 'Good',
  water: 'Water',
  guideVarietyUnknown: 'variety not known',
  guideGeneralPlan: 'GENERAL PLAN',
  guideDayAfter: 'Day {n} after sowing',
  guideSowingIn: 'Sowing in {n} days',
  guideSowingToday: 'Sowing day',
  guideEstimatedFrom: '· estimated from {what}',
  guideYourSowingDate: 'your sowing date',
  guideYourExpectedDate: 'your expected sowing date',
  guideSown: 'Sown {date}',
  guideExpectedSowing: 'Expected sowing {date}',
  guideBeforeSowing: 'Before sowing',
  guideHarvestAt: 'Harvest ~ Day {n}',
  cropName_wheat: 'Wheat',
  cropName_mustard: 'Mustard',
  cropName_rice: 'Rice',
  cropName_paddy: 'Paddy',
  cropName_maize: 'Maize',
  cropName_potato: 'Potato',
  cropName_sugarcane: 'Sugarcane',
  cropStage_before: 'Before sowing',
  cropStage_sowing: 'Sowing & sprouting',
  cropStage_growing: 'Growing',
  cropStage_harvest: 'Harvest',
  cropStage_notStarted: 'Not started yet',
  cropStage_finished: 'Season finished',
  cropStage_wheatSprouting: 'Sowing & sprouting',
  cropStage_wheatCrownRoots: 'Crown roots & first watering',
  cropStage_wheatSpreading: 'Plants spreading',
  cropStage_wheatStem: 'Stem growing',
  cropStage_wheatFlowering: 'Ears & flowering',
  cropStage_wheatGrainFilling: 'Grain filling',
  cropStage_mustardSprouting: 'Sowing & sprouting',
  cropStage_mustardThinning: 'Thinning & first watering',
  cropStage_mustardBranching: 'Branching',
  cropStage_mustardFlowering: 'Flowering',
  cropStage_mustardPods: 'Pods filling',
  cropStage_riceNursery: 'Nursery & puddling',
  cropStage_riceSettling: 'Transplanting & settling',
  cropStage_riceTillering: 'Tillering',
  cropStage_ricePanicle: 'Panicle forming',
  cropStage_riceFlowering: 'Flowering',
  cropStage_riceGrainFilling: 'Grain filling',
  cropStage_maizeSprouting: 'Sowing & sprouting',
  cropStage_maizeEarly: 'Early growth',
  cropStage_maizeKneeHigh: 'Knee-high',
  cropStage_maizePreTassel: 'Before tassels',
  cropStage_maizeTasselSilk: 'Tassels & silk',
  cropStage_maizeGrainFilling: 'Grain filling',
  cropStage_potatoSprouting: 'Planting & sprouting',
  cropStage_potatoVegetative: 'Leaf growth',
  cropStage_potatoTuberInit: 'Tubers starting',
  cropStage_potatoBulking: 'Tubers bulking',
  cropStage_potatoMaturing: 'Maturing',
  cropStage_caneSprouting: 'Planting & sprouting',
  cropStage_caneEarly: 'Early growth',
  cropStage_caneTillering: 'Tillering',
  cropStage_caneGrandGrowth: 'Grand growth',
  cropStage_caneMaturity: 'Ripening',
  satCloudy: 'Cloudy',
  satUnknown: 'No reading',
  sigWaterTitle: 'Water stress',
  sigGrowthTitle: 'Crop growth',
  sigWaterSource: 'Sentinel-2 · leaf water (NDMI) · {date}',
  sigGrowthSource: 'Sentinel-2 · greenness (NDVI) · {date}',
  sigGrowthSourceBoth: 'Sentinel-2 · greenness (NDVI) and leaf colour (NDRE) · {date}',
  sigTooCloudy: 'Too cloudy to read on {date}',
  sigNoReading: 'No satellite reading yet',
  waterPlaceDry: '{place} is dry',
  waterPlaceWet: '{place} is wet',
  waterEven: 'Water is even across the field',
  waterGoodExcept: 'Mostly fine — keep an eye on {place}',
  waterDryShare: 'Dry across {pct}% of the field',
  waterWetShare: 'Too wet across {pct}% of the field',
  waterDetailGood: 'Leaves across {pct}% of the field hold enough water. Most of it reads “{topClass}”.',
  waterDetailShares: 'The satellite reads {dryPct}% of the field as dry and {wetPct}% as too wet.',
  waterDetailDry: 'The satellite reads {dryPct}% of the field as dry.',
  waterDetailWet: 'The satellite reads {wetPct}% of the field as too wet.',
  todoWaterGood: 'Nothing to do. Keep your usual watering.',
  todoWaterGoodLook: 'Nothing urgent. When you walk the field, look at {place}.',
  todoWaterDry: 'Check the soil in {place} by hand. If it is dry below the surface, give it the next watering turn.',
  todoWaterWatchDry: 'Check the soil in {place} by hand in the next few days.',
  todoWaterWet: 'Skip watering {place} this turn, and make sure water can drain out.',
  growthPlaceWeak: 'Weaker growth in {place}',
  growthLeafPale: 'Leaves look pale in {pct}% of the field',
  growthEven: 'Growing evenly across the field',
  growthGoodExcept: 'Growing well, a little weaker in {place}',
  growthWeakShare: 'Weaker growth in {pct}% of the field',
  growthDetail: '{goodPct}% of the field is growing well; {weakPct}% is weaker.',
  growthDetailLeaf: 'Leaf colour is pale in {pct}% of the field, which can mean the crop is short of nitrogen.',
  todoGrowthGood: 'Nothing to do. Keep your usual schedule.',
  todoGrowthGoodLook: 'Nothing urgent. When you walk the field, look at the plants in {place}.',
  todoGrowthLeaf: 'Look at the lower leaves. If they are yellow, book a soil test before adding fertiliser.',
  todoGrowthWeak: 'Walk to {place} and compare the plants with the rest of the field. A soil test will show what is missing.',
  todoGrowthWatch: 'Keep an eye on {place} when you next walk the field.',
  verdictUnknown: 'No satellite reading yet',
  verdictSubUnknown: 'We will show your field as soon as a clear satellite picture comes in.',
  verdictAllNormal: 'All looks normal',
  verdictOneThing: '1 thing needs attention',
  verdictNThings: '{n} things need attention',
  placeNorthWest: 'the north-west corner',
  placeNorth: 'the north edge',
  placeNorthEast: 'the north-east corner',
  placeWest: 'the west edge',
  placeCentre: 'the middle of the field',
  placeEast: 'the east edge',
  placeSouthWest: 'the south-west corner',
  placeSouth: 'the south edge',
  placeSouthEast: 'the south-east corner',
  placeNorthSide: 'the north side',
  placeMiddleStrip: 'the middle strip',
  placeSouthSide: 'the south side',
  placeWestSide: 'the west side',
  placeCentreStrip: 'the centre strip',
  placeEastSide: 'the east side',
  placeMostOfField: 'most of the field',
  placeWholeField: 'the whole field',
  patchNameNoArea: 'Patch {letter} · {place}',
  satPictureDate: 'Picture of {date}',
  satLoadFailed: 'Could not load the satellite picture.',
  satRetry: 'Try again',
  mapSpotFine: 'Nothing to do here.',
  mapSpotCloud: 'Cloud covered this spot in the picture, so it could not be read.',
  mapShareOfField: '{pct}% of the field · {acres} ac',
  mapShareOfFieldNoArea: '{pct}% of the field',
  mapClose: 'Close',
  placeThisSpot: 'this spot',
  seasonTitle: 'Season by season',
  seasonScoreLabel: 'Crop greenness',
  seasonNow: 'Now',
  seasonTrendUp: 'Slowly getting better',
  seasonTrendDown: 'Weaker than past seasons',
  seasonTrendFlat: 'About the same as past seasons',
  seasonTrendFlatGood: 'Steady and healthy',
  seasonTrendUnknown: 'Not enough seasons to compare yet',
  seasonSubN: '{n} seasons of pictures, the same months each year',
  seasonSubNone: 'No season could be read yet',
  seasonWeakest: '{year} was the weakest',
  seasonScaleNote: 'Each bar is the average crop greenness (NDVI × 100) for these months in that year.',
  seasonMissing: 'No clear pictures',
  seasonLoadFailed: 'Could not load past seasons.',
  placeRestOfField: 'the rest of the field',
  mfSatFound: 'What the satellite found',
  mfLayerGrowth: 'Leaf cover',
  mfLayerNdre: 'Leaf nitrogen',
  mfLayerNdwi: 'Water stress',
  mfLayerWater: 'Moisture',
  mfPictureLoading: 'Getting the satellite picture…',
  mfToneGood: 'Fine',
  mfToneWatch: 'Watch',
  mfToneBad: 'Act now',
  mfToneGoodWord: 'Fine',
  mfToneWatchWord: 'Watch',
  mfToneBadWord: 'Act now',
  mfWatchTitleAll: 'What the satellite measures',
  mfWhat_growth: 'How much of the ground the green leaves cover',
  mfWhat_ndre: 'How green the leaves are inside — a sign of nitrogen',
  mfWhat_water: 'The moisture the satellite reads in the crop',
  mfWhat_ndwi: 'The water the satellite reads in the crop canopy — a sign of water stress, not a soil-moisture reading',
  mfWhat_ndviDecline: 'Leaves yellow slowly as the crop fills. Fast drying can mean pests, disease or thirst.',
  mfWhat_thermal: 'Hot leaves mean the plant has no water left to cool itself',
  mfRawClear: 'Clear {clear}% · cloud {cloud}%',
  mfShowNumbers: 'Show the numbers',
  mfHideNumbers: 'Hide the numbers',
  mfRightNow: 'Right now',
  mfWhatToDoHere: 'What to do here',
  mfReadingsNote: 'The other pictures are read at the middle of this patch.',
  sigNdwiTitle: 'Water stress (NDWI)',
  sigNdwiSource: 'Sentinel-2 · water stress (NDWI) · {date}',
  sigNdreTitle: 'Leaf nitrogen (NDRE)',
  sigNdreSource: 'Sentinel-2 · leaf colour (NDRE) · {date}',
  ndreEven: 'Leaf colour is even across the field',
  ndreGoodExcept: 'Leaf colour is fine, a little pale in {place}',
  ndrePlacePale: 'Leaves look pale in {place}',
  ndreDetail: 'The satellite reads {pct}% of the field as pale.',
};

/**
 * Replaces {token} placeholders. Missing tokens are left untouched so a bad
 * lookup shows the template rather than "undefined".
 */
const interpolate = (template, vars) => {
  if (!vars) return template;
  return String(template).replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  );
};

/** One string by its key, with its placeholders filled. An unknown key shows as itself. */
export const t = (key, vars) => interpolate(EN[key] ?? key, vars);

/** A satellite date as a day and short month, read in UTC like every other. */
export const satDate = (iso) => {
  if (!iso) return '';
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
};

/**
 * CropGen's class name as we word it: "Very Dry" looks up `cgVeryDry`. A
 * class we have no word for is shown as CropGen wrote it.
 */
export const classLabel = (label) => {
  if (!label) return '';
  const key = `cg${String(label).replace(/[^A-Za-z]/g, '')}`;
  const translated = t(key);
  return translated === key ? label : translated;
};

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const resolveParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([name, value]) => {
    if (name.endsWith('Key') && typeof value === 'string') {
      const base = name.slice(0, -3);
      // English has no oblique forms; `{placeObl}` reads as the plain place.
      out[base] = t(value);
      out[`${base}Obl`] = out[base];
    } else if (name === 'topClass') {
      out.topClass = classLabel(value);
    } else if (name === 'date') {
      out.date = satDate(value);
    } else {
      out[name] = value;
    }
  });
  return out;
};

/** A model text as words: '' for a missing text. */
export const renderSatText = (text) => {
  if (!text) return '';
  if (Array.isArray(text.parts)) {
    return text.parts.map((part) => renderSatText(part)).filter(Boolean).join(' · ');
  }
  if (!text.key) return '';
  return capitalise(t(text.key, resolveParams(text.params)));
};

/** Marker, dot and status colours, as on the farmer app's map. */
export const TONE_COLOURS = { bad: '#B8443A', watch: '#B7791F', good: '#2D7D46', neutral: '#6B7280' };
export const toneColour = (tone) => TONE_COLOURS[tone] || TONE_COLOURS.neutral;

/** The short status word under a reading: Fine / Watch / Act now. */
export const toneWord = (tone) =>
  t({ good: 'mfToneGood', watch: 'mfToneWatch', bad: 'mfToneBad', cloud: 'satCloudy', cloudy: 'satCloudy' }[tone] || 'satUnknown');

/** A layer's name on screen. */
export const layerLabel = (id) => (MAP_LAYERS[id] ? t(MAP_LAYERS[id].labelKey) : id);
